import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Download, Users, Star } from 'lucide-react';

const MembersArea = () => {
  const { user } = useAuth();

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const resources = [
    {
      icon: BookOpen,
      title: 'Exclusive Tutorials',
      description: 'Access premium coding tutorials and advanced guides',
      link: '#'
    },
    {
      icon: Download,
      title: 'Code Templates',
      description: 'Download production-ready code templates and boilerplates',
      link: '#'
    },
    {
      icon: Users,
      title: 'Community Access',
      description: 'Join our private Discord community for members',
      link: '#'
    },
    {
      icon: Star,
      title: 'Premium Content',
      description: 'Early access to new articles and video content',
      link: '#'
    }
  ];

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom max-w-6xl">
        {/* Welcome Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="mb-12"
        >
          <h1 className="mb-4">Welcome, {user?.name || 'Member'}!</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            You now have access to exclusive content and resources.
          </p>
        </motion.div>

        {/* User Info Card */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
          className="card mb-12 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900"
        >
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full border-2 border-white dark:border-gray-700"
              />
            )}
            <div>
              <h3 className="text-xl font-semibold">{user?.name}</h3>
              <p className="text-gray-600 dark:text-gray-400">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                {user?.role === 'admin' ? 'Admin' : 'Member'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {resources.map((resource, index) => (
            <motion.a
              key={resource.title}
              href={resource.link}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: index * 0.1 }}
              className="card group hover:scale-105"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <resource.icon className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-[rgb(var(--color-accent))] transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">{resource.description}</p>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Recent Activity */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mt-12"
        >
          <h2 className="text-2xl font-display font-semibold mb-6">Recent Activity</h2>
          <div className="card">
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="font-medium">Downloaded: React Advanced Patterns Template</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">2 days ago</p>
                </div>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <p className="font-medium">Completed: TypeScript Deep Dive Course</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">5 days ago</p>
                </div>
              </div>
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium">Joined: Members Discord Community</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">1 week ago</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MembersArea;
